import React, { useMemo } from "react";
import { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";

import { selectLayerConfigByKey, selectVersionConfig } from "fond/api";
import MultiLayerEditPanelContent from "fond/draw/MultiLayerEdit/MultiLayerEditPanelContent";
import { MultiLayerEditProvider } from "fond/draw/MultiLayerEdit/MultiLayerEditProvider";
import { DeleteButton, PolygonSelectButton, RedoButton, SelectButton, ToolbarConfig, UndoButton } from "fond/map/Toolbar";
import { MuPowerOutputLayerIds, MuPowerSolverLayerIds } from "fond/types";
import { SublayerConfig } from "fond/types/ProjectLayerConfig";
import { useAppSelector } from "fond/utils/hooks";
import { useStackedNavigationContext } from "fond/widgets";

import PhaseButton from "./PhaseButton";
import { SINGLE_PHASES } from "./phaseNetwork";
import { useDrawPhasePreview } from "./useDrawPhasePreview";
import { usePhaseNetwork } from "./usePhaseNetwork";

const toolbarConfig: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="selectPolygon" />],
  },
  {
    id: "phaseTools",
    type: "action",
    items: SINGLE_PHASES.map((phase) => <PhaseButton key={phase} phase={phase} />),
  },
  {
    id: "historyTools",
    type: "action",
    items: [<UndoButton key="undo" />, <RedoButton key="redo" />, <DeleteButton key="delete" />],
  },
];

const MuPowerPhaseOverridesDraw: React.FC = () => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { clear } = useStackedNavigationContext();

  const { network } = usePhaseNetwork(versionId);
  useDrawPhasePreview(network);

  const config = useAppSelector((state) => selectVersionConfig(state, versionId));
  const phaseOverridesConfig = useAppSelector((state) =>
    selectLayerConfigByKey(state, { versionId, layerKey: MuPowerSolverLayerIds.phaseOverrides })
  );
  const primaryWiresConfig = useAppSelector((state) => selectLayerConfigByKey(state, { versionId, layerKey: MuPowerOutputLayerIds.primaryWires }));

  /**
   * The visible layer ids (parents + their sublayers).
   */
  const visibleLayerIds = useMemo(() => {
    const layerIds: string[] = [MuPowerSolverLayerIds.phaseOverrides, MuPowerOutputLayerIds.primaryWires];

    [phaseOverridesConfig, primaryWiresConfig].forEach((layerConfig) => {
      layerConfig?.Children?.forEach((sublayerId) => {
        const sublayerConfig = config?.Data.entities[sublayerId] as SublayerConfig | undefined;
        if (sublayerConfig?.Type === "SUBLAYER") {
          layerIds.push(sublayerId);
        }
      });
    });

    return layerIds;
  }, [config, phaseOverridesConfig, primaryWiresConfig]);

  const options: MapboxDrawOptions = useMemo(
    () => ({
      editOptions: {
        allLayers: [
          {
            ID: MuPowerSolverLayerIds.phaseOverrides,
            GeometryType: "Point",
            getFeatureProperties: () => ({ layerId: MuPowerSolverLayerIds.phaseOverrides }),
          },
          // we use the primaryWires layer to preview the change in phase of the wires
          // see useDrawPhasePreview for details.
          {
            ID: MuPowerOutputLayerIds.primaryWires,
            GeometryType: "LineString",
            Hide: true,
          },
        ],
        editingLayerId: MuPowerSolverLayerIds.phaseOverrides,
        editingLayerIds: [MuPowerSolverLayerIds.phaseOverrides],
      },
      visibleLayerIds,
    }),
    [visibleLayerIds]
  );

  return (
    <MultiLayerEditProvider versionId={versionId} options={options} config={toolbarConfig}>
      <MultiLayerEditPanelContent
        title="Edit phase overrides"
        alertTitle="Select an override point and choose a phase to change the phase carried by its downstream wires."
        onFinish={clear}
      />
    </MultiLayerEditProvider>
  );
};

export default MuPowerPhaseOverridesDraw;
